import { VALUE_TYPE } from "c8-platform";
import NumberComponent, { NumberValueType } from "csi/components/number/NumberComponent";
import { useFormStore, useStatefulFormField } from "csi/middleware/form/index";
import { DEFAULT_DECIMAL_FORMAT, DEFAULT_INTEGER_FORMAT } from "csi/utility/numberUtility";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent } from "react";
import { useComponentOverlayContext } from "../hooks";

const FormFieldNumber: FunctionComponent = observer(() => {
	const isParentOverlay = useComponentOverlayContext();
	const field = useStatefulFormField();
	const formStore = useFormStore();
	const {
		attributeExpression,
		format,
		id,
		label,
		tooltip,
		valueType,
		transientInfo: {
			disabled,
			expressionDisabled,
			expressionValue,
			readOnly,
			required,
			value,
		},
	} = field;
	const [fieldInvalid, fieldMessage] = formStore?.getFieldError(id) || [false, ""];
	const isInteger = valueType === VALUE_TYPE.INTEGER;
	const numberFormat = format ?? (isInteger ? DEFAULT_INTEGER_FORMAT : DEFAULT_DECIMAL_FORMAT);
	const handleNumberChange = (value: number, e: React.FocusEvent<HTMLInputElement, Element>): void => {
		// we are passing null instead of e because we already have value and e could not have value in case of increment/decrement buttons
		formStore?.handleFieldChange(null, field, value);
	};

	const handleExpression = (): void => {
		field.transientInfo.expressionDisabled = !field.transientInfo.expressionDisabled;

		// user restores the expression
		if (!field.transientInfo.expressionDisabled) {
			field.transientInfo.value = field.transientInfo.expressionValue;
		}

		formStore?.updateForm(field);
	};

	/** internal DS validation callback - only set error message if there is no error from the server */
	const onValidation = (errorMessage: string) => {
		formStore?.setClientErrorField(field.id, errorMessage);
	};

	return (
		<NumberComponent
			dataCsiAutomation={id}
			disabled={disabled}
			error={fieldInvalid}
			errorMessage={fieldMessage}
			expressionButtonProps={
				attributeExpression && {
					expressionActive: !expressionDisabled,
					expressionValue,
					onChangeExpressionState: attributeExpression ? handleExpression : null,
				}
			}
			format={numberFormat}
			label={label}
			onBlurChange={handleNumberChange}
			onValidation={onValidation}
			readOnly={readOnly}
			required={required}
			tabIndex={isParentOverlay ? -1 : undefined}
			tooltip={tooltip}
			value={formStore ? value as number : null}
			valueType={valueType as NumberValueType}
		/>
	);
});

export default FormFieldNumber;
